// Formulario de la regla de cobranza de UN comercio (plazo propio, pausa de automáticos,
// correos y teléfono de cobranza). Funciones puras.
//
// Límites iguales al DTO de billing (SaveCommerceCollectionRuleDto): se avisan en el campo;
// billing revalida y además normaliza el teléfono con el país del operador.

import { apiErrorMessage, apiErrorStatus } from './collectionReminders';

export const MIN_PAYMENT_TERM_DAYS = 1;
export const MAX_PAYMENT_TERM_DAYS = 365;
export const MAX_CONTACT_EMAILS = 10;
export const MAX_CONTACT_PHONE_LENGTH = 32;

// Correo: mismas reglas que `isEmail` de la librería validator con sus opciones por defecto,
// que es lo que aplica @IsEmail() en billing. Una regla más laxa dejaba pasar
// 'pagos@comercio.c' o 'juan..perez@x.cl' y billing respondía 400 con un texto técnico en
// inglés. Única diferencia: una parte local entre comillas ("juan perez"@x.cl), válida para
// billing, acá se rechaza; no se usa en correos de cobranza.
const MAX_EMAIL_LENGTH = 254;
const MAX_EMAIL_USER_BYTES = 64;
const MAX_EMAIL_DOMAIN_BYTES = 254;
const MAX_DOMAIN_LABEL_LENGTH = 63;
const EMAIL_USER_PART = /^[a-z\d!#$%&'*+/=?^_`{|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF-]+$/i;
const DOMAIN_TLD = /^([a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i;
const DOMAIN_LABEL = /^[a-z\u00a1-\uffff0-9-]+$/i;
const FULL_WIDTH_CHARS = /[\uff01-\uff5e]/;

// Largo en bytes UTF-8 (mismo cálculo que validator). null si el texto no es codificable.
const utf8ByteLength = (value) => {
  try {
    return encodeURI(value).split(/%..|./).length - 1;
  } catch (err) {
    if (err instanceof URIError) return null;
    throw err;
  }
};

const isValidEmailDomain = (domain) => {
  const labels = domain.split('.');
  if (labels.length < 2 || !DOMAIN_TLD.test(labels[labels.length - 1])) return false;
  return labels.every(
    (label) =>
      label.length <= MAX_DOMAIN_LABEL_LENGTH &&
      DOMAIN_LABEL.test(label) &&
      !FULL_WIDTH_CHARS.test(label) &&
      !label.startsWith('-') &&
      !label.endsWith('-'),
  );
};

/** ¿Es un correo que billing acepta? (ver arriba). Espera el correo ya normalizado. */
export function isValidEmail(email) {
  const value = String(email ?? '');
  if (value.length > MAX_EMAIL_LENGTH) return false;
  const at = value.lastIndexOf('@');
  if (at <= 0) return false;
  const user = value.slice(0, at);
  const domain = value.slice(at + 1);
  const userBytes = utf8ByteLength(user);
  const domainBytes = utf8ByteLength(domain);
  if (userBytes === null || userBytes > MAX_EMAIL_USER_BYTES) return false;
  if (domainBytes === null || domainBytes > MAX_EMAIL_DOMAIN_BYTES) return false;
  if (!isValidEmailDomain(domain)) return false;
  return user.split('.').every((part) => EMAIL_USER_PART.test(part));
}

// Teléfono tal como se escribe: dígitos, separadores habituales y un '+' inicial. Billing lo
// convierte a E.164 con el país del operador ('9 4240 5123', '011 15 1234-5678').
const PHONE_INPUT_RE = /^\+?[\d\s().-]*$/;

const isTermDays = (v) =>
  Number.isInteger(v) && v >= MIN_PAYMENT_TERM_DAYS && v <= MAX_PAYMENT_TERM_DAYS;

/** trim + minúsculas (mismo criterio que billing para deduplicar). */
export const normalizeEmail = (value) => String(value ?? '').trim().toLowerCase();

/** Estado del formulario desde `rule` de GET commerce-rules/:companyId. */
export function ruleToForm(rule) {
  const days = rule?.paymentTermDays;
  return {
    ownTerm: isTermDays(days),
    termDays: isTermDays(days) ? String(days) : '',
    remindersPaused: rule?.remindersPaused === true,
    contactEmails: Array.isArray(rule?.contactEmails) ? [...rule.contactEmails] : [],
    contactPhone: rule?.contactPhone ?? '',
  };
}

/**
 * ¿Se puede agregar este correo a la lista? Devuelve la clave i18n del problema o null.
 * @param {string} email ya normalizado
 * @param {string[]} current correos ya cargados
 */
export function emailToAddError(email, current) {
  if (!email) return 'Escribí un correo.';
  if (!isValidEmail(email)) return 'Ese correo no es válido.';
  if (current.includes(email)) return 'Ese correo ya está en la lista.';
  if (current.length >= MAX_CONTACT_EMAILS) return 'Máximo 10 correos de cobranza.';
  return null;
}

/**
 * ¿Es el 400 de billing por un correo inválido? class-validator lo devuelve en inglés
 * ('each value in contactEmails must be an email') y sin decir cuál: el formulario lo
 * reemplaza por un texto propio.
 */
export function isContactEmailsRejection(err) {
  return apiErrorStatus(err) === 400 && /\bcontactEmails\b/.test(apiErrorMessage(err) ?? '');
}

/** Errores por campo (claves i18n): `{ termDays?, contactPhone? }`. */
export function validateRuleForm(form) {
  const errors = {};
  if (form.ownTerm && !isTermDays(Number(form.termDays))) {
    errors.termDays = 'Ingresá un plazo entre 1 y 365 días.';
  }
  const phone = String(form.contactPhone ?? '').trim();
  if (phone.length > MAX_CONTACT_PHONE_LENGTH) {
    errors.contactPhone = 'El teléfono es demasiado largo.';
  } else if (!PHONE_INPUT_RE.test(phone)) {
    errors.contactPhone =
      'El teléfono solo puede tener números, espacios, guiones, paréntesis y un + inicial.';
  }
  return errors;
}

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Body PARCIAL del PUT: solo los campos que cambiaron respecto de lo guardado. Reenviar un
 * teléfono sin tocarlo lo volvería a normalizar (y un override viejo inválido, que billing
 * respeta al leer, respondería 400 al guardar otro campo).
 *
 * @returns {object} vacío si no hay cambios
 */
export function buildRulePayload(form, initial) {
  const payload = {};
  const termNow = form.ownTerm ? Number(form.termDays) : null;
  const termBefore = initial.ownTerm ? Number(initial.termDays) : null;
  if (termNow !== termBefore) payload.paymentTermDays = termNow;
  if (form.remindersPaused !== initial.remindersPaused) {
    payload.remindersPaused = form.remindersPaused;
  }
  if (!sameList(form.contactEmails, initial.contactEmails)) {
    payload.contactEmails = [...form.contactEmails];
  }
  const phoneNow = String(form.contactPhone ?? '').trim();
  const phoneBefore = String(initial.contactPhone ?? '').trim();
  if (phoneNow !== phoneBefore) payload.contactPhone = phoneNow === '' ? null : phoneNow;
  return payload;
}
